import ReconnectingWebSocket from 'reconnecting-websocket';
import { v4 as uuid } from 'uuid';

import { proxyAxios } from 'api/apiBase/axios';
import { IOrderbook, ITickerSocket, TradeSocket } from 'types/cex/spot';
import { cexExchangesTimeframes, IChartTimeframe, KLine } from 'types/charts';
import { normalizeNumber } from 'utils/formats/normalizeNumber';
import { ECexName } from 'web3';

import { BaseStrategy, IInitArgs } from '../base_strategy';
import { bootstrapStrategy } from '../bootstrap';
import { aggregateOrderbook, mergeOrderBook, sortOrderbookFunc } from '../utils';

export class KucoinStrategy implements BaseStrategy {
  ws: ReconnectingWebSocket | undefined;
  pingInterval: ReturnType<typeof setInterval> | undefined;

  bids: [string, string][];
  asks: [string, string][];
  pair: string;
  depth: number;
  precision: number;
  onOrderbookUpdate: (v: IOrderbook) => void;
  onTickerUpdate: (v: ITickerSocket) => void;
  klineUpdate: (v: KLine) => void;
  precisionUpdate: (v: number) => void;
  onTradeUpdate: (v: TradeSocket) => void;

  selectedPrecision: number;

  constructor({
    base,
    quote,
    orderbookDepth,
    onOrderbookUpdate,
    onTickerUpdate,
    klineUpdate,
    precisionUpdate,
    onTradeUpdate,
  }: IInitArgs) {
    this.pair = `${base.toUpperCase()}-${quote.toUpperCase()}`;
    this.bids = [];
    this.asks = [];
    this.depth = orderbookDepth ?? 20;
    this.precision = 1;
    this.selectedPrecision = 1;
    this.onOrderbookUpdate = onOrderbookUpdate;
    this.onTickerUpdate = onTickerUpdate;
    this.klineUpdate = klineUpdate;
    this.precisionUpdate = precisionUpdate;
    this.onTradeUpdate = onTradeUpdate;
  }

  setOrderbookDepth(depth: number) {
    this.depth = depth;
  }

  fetchTickerPrecision = async () => {
    try {
      const url = new URL(`https://api.kucoin.com/api/v2/symbols/${this.pair}`);

      const res = await proxyAxios.get<{ data: { priceIncrement: string } }>('/api/v1/proxy', {
        params: {
          url: url.toString(),
        },
      });

      let prec = 1;

      if (res.data) {
        prec = res.data.data.priceIncrement.split('.')[1].length;
      }

      this.precision = prec;
      this.selectedPrecision = prec;
      this.precisionUpdate(prec);
    } catch (error) {
      console.log('Kucoin ticker error: ', error);
    }
  };

  fetchOrderbook = async () => {
    try {
      const url = new URL(
        `https://api.kucoin.com/api/v1/market/orderbook/level2_100?symbol=${this.pair}`,
      );
      const params = new URLSearchParams({
        symbol: this.pair,
      });

      url.search = params.toString();

      const res = await proxyAxios.get<{
        data: { asks: [string, string][]; bids: [string, string][] };
      }>('/api/v1/proxy', {
        params: {
          url: url.toString(),
        },
      });

      const { asks, bids } = res.data.data;

      if (asks || bids) {
        this.asks = asks;
        this.bids = bids;

        const orderbook = {
          bids: bids.map(el => [Number(el[0]).toFixed(this.selectedPrecision), el[1]]),
          asks: asks.map(el => [Number(el[0]).toFixed(this.selectedPrecision), el[1]]),
        };

        this.onOrderbookUpdate(
          aggregateOrderbook(orderbook.asks, orderbook.bids, this.selectedPrecision),
        );

        return orderbook;
      }
    } catch (error) {
      console.log('Kucoin orderbook error: ', error);
    }
  };

  fetchKlines = async ({
    base,
    quote,
    limit,
    interval,
  }: {
    base: string;
    quote: string;
    limit: number;
    interval: IChartTimeframe;
  }) => {
    try {
      const innerInterval = cexExchangesTimeframes[interval][ECexName.kucoin];

      const url = new URL('https://api.kucoin.com/api/v1/market/candles');
      const params = new URLSearchParams({
        symbol: `${base}-${quote}`,
        type: innerInterval,
      });

      url.search = params.toString();

      const result = await proxyAxios.get<{
        data?: string[][];
      }>('/api/v1/proxy', {
        params: {
          url: url.toString(),
        },
      });

      return (result.data.data ?? [])
        .map(el => [Number(el[0]) * 1000, el[1], el[3], el[4], el[2], el[5]])
        .reverse()
        .slice(-limit) as KLine[];
    } catch (error) {
      console.log('Kucoin klines error: ', error);

      return [];
    }
  };

  fetchTrades = async ({ base, quote, limit }: { base: string; quote: string; limit: number }) => {
    try {
      const url = new URL('https://api.kucoin.com/api/v1/market/histories');
      const params = new URLSearchParams({ symbol: `${base}-${quote}` });

      url.search = params.toString();

      const result = await proxyAxios.get<{
        data?: {
          side: string;
          price: string;
          size: string;
          time: string;
        }[];
      }>('/api/v1/proxy', { params: { url: url.toString() } });

      return (result.data.data ?? [])
        .map(item => ({
          price: item.price,
          quantity: item.size,
          timestamp: item.time,
          side: item.side,
        }))
        .slice(0, limit - 1);
    } catch (error) {
      console.log('Kucoin trades error: ', error);
      return [];
    }
  };

  connectWebSocket = async ({ interval }: { interval: number }) => {
    try {
      if (this.ws) {
        this.closeWebSocket();
      }

      const {
        data: {
          data: { token },
        },
      } = await proxyAxios.post<{ data: { token: string } }>('/api/v1/proxy', {
        url: 'https://api.kucoin.com/api/v1/bullet-public',
        method: 'POST',
      });

      const ws = new ReconnectingWebSocket(`wss://ws-api.kucoin.com/endpoint?token=${token}`, [], {
        maxRetries: 10,
        minReconnectionDelay: 1000,
      });

      this.ws = ws;

      const intervalSlug = {
        60000: '1min',
        300000: '5min',
        900000: '15min',
        3600000: '1hour',
        14400000: '4hour',
        86400000: '1day',
        604800000: '1week',
      }[interval];

      const tickerTopic = `/market/snapshot:${this.pair}`;
      const klinesTopic = `/market/candles:${this.pair}_${intervalSlug}`;
      const depthTopic = `/market/level2:${this.pair}`;
      const tradesTopic = `/market/match:${this.pair}`;

      this.pingInterval = setInterval(() => {
        if (this.ws) {
          this.ws.send(
            JSON.stringify({ id: Math.floor(Date.now() / 100).toString(), type: 'ping' }),
          );
        }
      }, 60 * 1000);

      this.ws.onopen = () => {
        const tickerId = Date.now();
        const klinesId = tickerId + 1;

        ws.send(
          JSON.stringify({
            id: uuid(),
            type: 'message',
            topic: depthTopic,
            response: true,
          }),
        );

        ws.send(
          JSON.stringify({
            id: tickerId,
            type: 'subscribe',
            topic: tickerTopic,
            response: true,
          }),
        );

        ws.send(
          JSON.stringify({
            id: klinesId,
            type: 'subscribe',
            topic: klinesTopic,
            response: true,
          }),
        );

        ws.send(
          JSON.stringify({
            id: Date.now(),
            type: 'subscribe',
            topic: tradesTopic,
            response: true,
          }),
        );
      };

      this.ws.onmessage = event => {
        try {
          const data = JSON.parse(event.data);

          if (data.topic === depthTopic) {
            const { asks: responseAsks, bids: responseBids } = data.data.changes;

            const asks = mergeOrderBook(this.asks, responseAsks).sort(sortOrderbookFunc);
            const bids = mergeOrderBook(this.bids, responseBids).sort(sortOrderbookFunc);

            this.asks = asks;
            this.bids = bids;

            this.onOrderbookUpdate(
              aggregateOrderbook(
                asks.map(el => [Number(el[0]).toFixed(this.precision), el[1]]),
                bids.map(el => [Number(el[0]).toFixed(this.precision), el[1]]),
                this.selectedPrecision,
              ),
            );
          }

          if (data.topic === tickerTopic) {
            const { vol, close, high, low, volValue } = data.data.data;

            const change = data.data.data.marketChange24h.changeRate;

            this.onTickerUpdate({
              baseVolume: normalizeNumber(vol),
              change: normalizeNumber(change * 100),
              close: normalizeNumber(close),
              high: normalizeNumber(high),
              last: close.toFixed(this.precision),
              low: normalizeNumber(low),
              quoteVolume: normalizeNumber(volValue),
            });
          }

          if (data.topic === klinesTopic) {
            const [time, open, close, high, low, volume] = data.data.candles;

            this.klineUpdate([Number(time), open, high, low, close, volume]);
          }

          if (data.topic === tradesTopic) {
            const { price, size, time, side } = data.data;

            this.onTradeUpdate({
              price,
              quantity: size,
              timestamp: time,
              side,
            });
          }
        } catch (error) {
          console.log('Kucoin socket error', error);
        }
      };
    } catch (error) {
      console.log('Kucoin socket connect error: ', error);
    }
  };

  closeWebSocket = () => {
    if (this.ws) {
      this.ws.close();
    }
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
    }
  };

  switchOrderbookTick = ({ tick }: { tick: number }) => {
    this.selectedPrecision = tick;

    this.onOrderbookUpdate(
      aggregateOrderbook(
        this.asks.map(el => [Number(el[0]).toFixed(this.precision), el[1]]),
        this.bids.map(el => [Number(el[0]).toFixed(this.precision), el[1]]),
        this.selectedPrecision,
      ),
    );
  };

  getOrderbookTicks = (prec: number) =>
    Array.from({ length: prec })
      .fill(0)
      .map((_, idx) => idx + 1)
      .map(el => ({ value: normalizeNumber(1 / 10 ** el), tick: el, id: el }))
      .slice(-3);

  fetch = async () => {
    await this.fetchTickerPrecision();
    await this.fetchOrderbook();
  };

  static init = async (args: IInitArgs) => {
    const instance = new KucoinStrategy(args);

    await bootstrapStrategy(instance);

    return instance;
  };
}
